import { createContext } from 'react'
import type { IObservableArray } from 'mobx'
import { action, makeObservable, observable } from 'mobx'

export type DialogBuilder = ({
  remove,
}: {
  remove: () => void
}) => React.ReactNode

export type DialogConfig = {
  namespace?: string
  ignoreOnNamespace?: boolean
  trigger?: HTMLElement | EventTarget
}

export type DialogStoreEntry = {
  id: number
  builder: DialogBuilder
  config: DialogConfig
  remove: () => void
  namespace?: string
}

type DialogContextType = {
  dialogStore: DialogStore
  showDialog: (
    dialog: DialogBuilder,
    config?: DialogConfig
  ) => DialogStoreEntry | undefined
  anyDialogsOpen: () => boolean
  popDialog: () => void
  removeDialog: (entry: DialogStoreEntry) => void
  clearAllDialogs: () => void
}

export const DialogContext = createContext<DialogContextType | null>(null)

export class DialogStore {
  dialogs: IObservableArray<DialogStoreEntry> =
    observable.array<DialogStoreEntry>([])
  refocusDelay = 300

  objectId = 0
  triggers = new Map<number, EventTarget | HTMLElement>()
  triggersIds = new Map<number, string>()

  constructor() {
    makeObservable(this, {
      dialogs: observable,
      // actions
      showDialog: action,
      pop: action,
      remove: action,
      clearAllDialogs: action,
    })
  }

  showDialog(dialog: DialogBuilder, config?: DialogConfig) {
    const safeConfig = config || {}
    const entry: DialogStoreEntry = {
      id: this.objectId++,
      builder: dialog,
      namespace: safeConfig.namespace,
      config: safeConfig,
      remove: () => this.remove(entry),
    }
    const currentlyFocusedElement = document.activeElement
    if (currentlyFocusedElement) {
      this.triggers.set(entry.id, currentlyFocusedElement)
      if (currentlyFocusedElement.id) {
        this.triggersIds.set(entry.id, currentlyFocusedElement.id)
      }
    }
    if (safeConfig.namespace && safeConfig.ignoreOnNamespace) {
      const existingDialog = this.dialogs.find(
        (d) => d.namespace === safeConfig.namespace
      )
      if (existingDialog) return
    }
    this.dialogs.push(entry)
    return entry
  }

  remove(entry: DialogStoreEntry) {
    const index = this.dialogs.findIndex((d) => d.id === entry.id)
    if (index !== -1) {
      const dialog = this.dialogs[index]
      this.dialogs.splice(index, 1)

      if (dialog) this.refocusTrigger(dialog)
    }
  }

  pop() {
    const last = this.dialogs.pop()
    if (last) this.refocusTrigger(last)
  }

  async refocusTrigger(entry: DialogStoreEntry) {
    await new Promise((resolve) => setTimeout(resolve, this.refocusDelay))
    const trigger = this.triggers.get(entry.id)
    const triggerId = this.triggersIds.get(entry.id)
    // if manual trigger is set, use that
    if (entry?.config.trigger) {
      const trigger = entry.config.trigger
      if (trigger instanceof HTMLElement) {
        trigger.focus()
        entry.config.trigger = undefined
      }
      // otherwise, if we have a automatic trigger element, use that
    } else if (triggerId) {
      const element = document.getElementById(triggerId)
      if (element) element.focus()
    } else if (trigger && trigger instanceof HTMLElement) {
      trigger.focus()
    }
    this.triggers.delete(entry.id)
    this.triggersIds.delete(entry.id)
  }

  clearAllDialogs() {
    this.dialogs.clear()
    this.triggers.clear()
  }
}
